<script setup>
import { computed, ref, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf, url } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FileInput from 'dashboard/components/widgets/conversation/WhatsappTemplates/FileInput.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['sendMessage', 'back']);

const MediaFormats = ['document', 'image', 'video'];

const { t } = useI18n();

const processedParams = ref({});
const headerProcessedParams = ref({});
const headerMediaUrl = ref('');
const headerComponent = ref(null);

const acceptedTypes = {
  image: 'image/*',
  video: 'video/*',
  document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
};

const templateName = computed(() => {
  return props.template?.name || '';
});

const templateString = computed(() => {
  if (!props.template || !props.template.components) {
    return '';
  }
  const bodyComponent = props.template.components.find(
    component => component.type === 'BODY'
  );
  return bodyComponent?.text || '';
});

const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

const variables = computed(() => {
  if (!templateString.value) return null;
  return templateString.value.match(/{{([^}]+)}}/g);
});

const headerVariables = computed(() => {
  const variables = headerString.value?.match(/{{([^}]+)}}/g);
  return variables;
});

const hasHeader = computed(() => {
  return !!headerComponent.value;
});

const hasMediaHeader = computed(() => {
  return (
    hasHeader.value &&
    MediaFormats.includes(headerComponent.value.format?.toLowerCase())
  );
});

const headerString = computed(() => {
  return headerComponent.value?.format?.toUpperCase() === 'TEXT'
    ? headerComponent.value.text
    : null;
});

const headerMediaType = computed(() => {
  return headerComponent.value?.format?.toUpperCase();
});

const processedString = computed(() => {
  if (!templateString.value) return '';
  return templateString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.value[variableKey] || `{{${variable}}}`;
  });
});

const headerProcessedString = computed(() => {
  if (!headerString.value) return '';
  return headerString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return headerProcessedParams.value[variableKey] || `{{${variable}}}`;
  });
});

const processedStringWithVariableHighlight = computed(() => {
  if (!templateString.value) return '';
  const variables = templateString.value.match(/{{([^}]+)}}/g) || [];

  return variables.reduce((result, variable) => {
    const variableKey = processVariable(variable);
    const value = processedParams.value[variableKey] || variable;
    return result.replace(
      variable,
      `<span class="break-all text-n-slate-12">${value}</span>`
    );
  }, templateString.value);
});

const acceptedType = computed(() => {
  return acceptedTypes[headerMediaType.value?.toLowerCase()];
});

const requireBodyVariables = computed(() => {
  return variables.value && variables.value.length > 0 && !hasMediaHeader.value;
});

const rules = computed(() => {
  const paramRules = {};
  Object.keys(processedParams.value).forEach(key => {
    paramRules[key] = {
      required: requiredIf(() => requireBodyVariables.value),
    };
  });
  return {
    processedParams: paramRules,
    headerMediaUrl: {
      required: requiredIf(() => hasMediaHeader.value),
      url: url,
    },
  };
});

const v$ = useVuelidate(rules, { processedParams, headerMediaUrl });

const getFieldErrorType = key => {
  if (!v$.value.processedParams[key]?.$error) return 'info';
  return 'error';
};

const generateVariables = () => {
  if (!templateString.value) return;

  const matchedVariables = templateString.value.match(/{{([^}]+)}}/g);
  if (!matchedVariables) return;

  const finalVars = matchedVariables.map(i => processVariable(i));
  processedParams.value = finalVars.reduce((acc, variable) => {
    acc[variable] = '';
    return acc;
  }, {});
};

const extractHeaderComponent = () => {
  if (!props.template || !props.template.components) return;

  headerComponent.value = props.template.components.find(
    component => component.type === 'HEADER'
  );

  if (headerComponent.value && headerString.value) {
    const matchedVariables = headerString.value.match(/{{([^}]+)}}/g);
    if (matchedVariables) {
      const finalVars = matchedVariables.map(i => processVariable(i));
      headerProcessedParams.value = finalVars.reduce((acc, variable) => {
        acc[variable] = '';
        return acc;
      }, {});
    }
  }
};

const onFileInput = fileUrl => {
  headerMediaUrl.value = fileUrl;
};

const buildWhatsAppTemplateComponent = () => {
  const components = [];

  if (hasMediaHeader.value && headerMediaUrl.value) {
    const headerComponent = {
      type: 'header',
      parameters: [
        {
          type: headerMediaType.value.toLowerCase(),
          [headerMediaType.value.toLowerCase()]: {
            link: headerMediaUrl.value,
          },
        },
      ],
    };
    components.push(headerComponent);
  }

  if (Object.keys(headerProcessedParams.value).length > 0) {
    const headerComponent = {
      type: 'header',
      parameters: Object.keys(headerProcessedParams.value).map(key => ({
        type: 'text',
        text: headerProcessedParams.value[key],
      })),
    };
    components.push(headerComponent);
  }

  if (Object.keys(processedParams.value).length > 0) {
    const bodyComponent = {
      type: 'body',
      parameters: Object.keys(processedParams.value).map(key => ({
        type: 'text',
        parameter_name: key,
        text: processedParams.value[key],
      })),
    };
    components.push(bodyComponent);
  }
  return components;
};

const sendMessage = async () => {
  if (!v$.value) {
    return;
  }

  // For media templates, skip validation if media is uploaded
  if (hasMediaHeader.value && headerMediaUrl.value) {
    // Media uploaded, proceed
  } else if (hasMediaHeader.value && !headerMediaUrl.value) {
    // Media required but not uploaded
    v$.value.$touch();
    return;
  } else {
    // Text template, validate all fields
    const isValid = await v$.value.$validate();
    if (!isValid) return;
  }

  const payload = {
    message: processedString.value,
    templateParams: {
      name: props.template.name,
      category: props.template.category,
      language: props.template.language,
      namespace: props.template.namespace,
      processed_params: processedParams.value,
      components: buildWhatsAppTemplateComponent(),
    },
  };
  emit('sendMessage', payload);
};

onMounted(() => {
  generateVariables();
  extractHeaderComponent();
});
</script>

<template>
  <div
    class="absolute top-full mt-1.5 max-h-[30rem] overflow-y-auto left-0 flex flex-col gap-4 px-4 pt-6 pb-5 items-start w-[28.75rem] h-auto bg-n-solid-2 border border-n-strong shadow-sm rounded-lg"
  >
    <span class="text-sm text-n-slate-12">
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.TEMPLATE_NAME',
          { templateName: templateName }
        )
      }}
    </span>

    <div v-if="hasHeader" class="w-full">
      <span class="text-sm font-medium text-n-slate-12">Header</span>

      <div v-if="hasMediaHeader" class="mt-2 flex items-center gap-2">
        <span
          class="bg-n-alpha-black2 text-n-slate-12 inline-block rounded-md text-xs py-2 px-4"
        >
          {{ headerMediaType }}
        </span>
        <FileInput :accept="acceptedType" @input="onFileInput" />
      </div>
      <p
        v-if="
          v$ &&
          v$.headerMediaUrl &&
          v$.headerMediaUrl.$dirty &&
          v$.headerMediaUrl.$invalid
        "
        class="mt-1 text-xs text-n-ruby-9"
      >
        Invalid URL
      </p>

      <div v-if="headerString" class="mt-2">
        <textarea
          :value="headerProcessedString"
          rows="3"
          readonly
          class="w-full p-2 text-sm bg-n-alpha-black2 rounded-md border border-n-strong text-n-slate-11"
        />
        <div v-if="headerVariables" class="mt-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.VARIABLES'
              )
            }}
          </span>
          <div
            v-for="(variable, key) in headerProcessedParams"
            :key="key"
            class="flex items-center w-full gap-2 mt-2"
          >
            <span
              class="flex items-center h-8 text-sm min-w-6 ltr:text-left rtl:text-right text-n-slate-10"
            >
              {{ key }}
            </span>
            <Input
              v-model="headerProcessedParams[key]"
              custom-input-class="!h-8 w-full !bg-transparent"
              class="w-full"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="w-full">
      <span class="text-sm font-medium text-n-slate-12">Body</span>
      <p
        class="mt-2 mb-0 text-sm text-n-slate-11"
        v-html="processedStringWithVariableHighlight"
      />
      <div v-if="hasMediaHeader && !variables" class="mt-2">
        <p class="text-sm text-n-slate-12 bg-n-slate-3 p-2 rounded">
          This media template doesn't require body variables. You can send it
          directly after uploading the media file.
        </p>
      </div>
    </div>

    <span
      v-if="Object.keys(processedParams).length"
      class="text-sm font-medium text-n-slate-12"
    >
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.VARIABLES'
        )
      }}
    </span>

    <div
      v-for="(variable, key) in processedParams"
      :key="key"
      class="flex items-center w-full gap-2"
    >
      <span
        class="flex items-center h-8 text-sm min-w-6 ltr:text-left rtl:text-right text-n-slate-10"
      >
        {{ key }}
      </span>
      <Input
        v-model="processedParams[key]"
        custom-input-class="!h-8 w-full !bg-transparent"
        class="w-full"
        :message-type="getFieldErrorType(key)"
      />
    </div>

    <p
      v-if="v$ && v$.$dirty && v$.$invalid"
      class="w-full text-xs text-center text-n-ruby-9 bg-n-ruby-9/20 rounded-md p-2"
    >
      {{
        hasMediaHeader
          ? 'Please upload a media file to send this template.'
          : 'Please fill in all required fields.'
      }}
    </p>

    <div class="flex items-end justify-between w-full gap-3 h-14">
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.BACK'
          )
        "
        color="slate"
        variant="faded"
        class="w-full font-medium"
        @click="emit('back')"
      />
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.SEND_MESSAGE'
          )
        "
        class="w-full font-medium"
        @click="sendMessage"
      />
    </div>
  </div>
</template>
